import apiClient from './client'
import type {
  ClienteProveedor,
  ClienteProveedorListItem,
  ClienteProveedorInput,
  Ingreso,
  IngresoListItem,
  IngresoInput,
  Gasto,
  GastoListItem,
  GastoInput,
  TesoreriaDashboardRow,
  TesoreriaImportSummary,
} from '@/types/gia'
import type { PaginatedResponse } from '@/types/gia'

const BASE = '/tesoreria'

const tesoreriaApi = {
  // Clientes/Proveedores
  listClientes: async (params: { q?: string; tipo?: 'cliente' | 'proveedor'; page?: number; page_size?: number } = {}): Promise<PaginatedResponse<ClienteProveedorListItem>> => {
    return (await apiClient.get(`${BASE}/clientes/`, { params })).data
  },
  getCliente: async (id: number): Promise<ClienteProveedor> =>
    (await apiClient.get(`${BASE}/clientes/${id}/`)).data,
  createCliente: async (data: ClienteProveedorInput): Promise<ClienteProveedor> =>
    (await apiClient.post(`${BASE}/clientes/`, data)).data,
  updateCliente: async (id: number, data: Partial<ClienteProveedorInput>): Promise<ClienteProveedor> =>
    (await apiClient.patch(`${BASE}/clientes/${id}/`, data)).data,
  deleteCliente: async (id: number): Promise<void> =>
    apiClient.delete(`${BASE}/clientes/${id}/`),

  // Ingresos
  listIngresos: async (params: {
    estado?: string; centro?: number; quincena?: string; year?: number; q?: string;
    fecha_desde?: string; fecha_hasta?: string; importe_min?: string; importe_max?: string;
    ordering?: string; page?: number; page_size?: number
  } = {}): Promise<PaginatedResponse<IngresoListItem>> =>
    (await apiClient.get(`${BASE}/ingresos/`, { params })).data,
  getIngreso: async (id: number): Promise<Ingreso> =>
    (await apiClient.get(`${BASE}/ingresos/${id}/`)).data,
  createIngreso: async (data: IngresoInput): Promise<Ingreso> => {
    const fd = _toFormData(data)
    return (await apiClient.post(`${BASE}/ingresos/`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })).data
  },
  updateIngreso: async (id: number, data: Partial<IngresoInput>): Promise<Ingreso> => {
    const fd = _toFormData(data)
    return (await apiClient.patch(`${BASE}/ingresos/${id}/`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })).data
  },
  deleteIngreso: async (id: number): Promise<void> =>
    apiClient.delete(`${BASE}/ingresos/${id}/`),
  exportIngresos: (params: Record<string, any> = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v != null).map(([k, v]) => [k, String(v)])).toString()
    window.open(`/api/${BASE}/ingresos/export/${qs ? '?' + qs : ''}`, '_blank')
  },

  // Gastos
  listGastos: async (params: {
    estado?: string; centro?: number; quincena?: string; year?: number; q?: string;
    fecha_desde?: string; fecha_hasta?: string; importe_min?: string; importe_max?: string;
    duplicados?: boolean; ordering?: string; page?: number; page_size?: number
  } = {}): Promise<PaginatedResponse<GastoListItem>> =>
    (await apiClient.get(`${BASE}/gastos/`, { params })).data,
  getGasto: async (id: number): Promise<Gasto> =>
    (await apiClient.get(`${BASE}/gastos/${id}/`)).data,
  createGasto: async (data: GastoInput): Promise<Gasto> => {
    const fd = _toFormData(data)
    return (await apiClient.post(`${BASE}/gastos/`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })).data
  },
  updateGasto: async (id: number, data: Partial<GastoInput>): Promise<Gasto> => {
    const fd = _toFormData(data)
    return (await apiClient.patch(`${BASE}/gastos/${id}/`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })).data
  },
  deleteGasto: async (id: number): Promise<void> =>
    apiClient.delete(`${BASE}/gastos/${id}/`),
  exportGastos: (params: Record<string, any> = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v != null).map(([k, v]) => [k, String(v)])).toString()
    window.open(`/api/${BASE}/gastos/export/${qs ? '?' + qs : ''}`, '_blank')
  },

  // Dashboard
  getDashboard: async (year?: number): Promise<TesoreriaDashboardRow[]> =>
    (await apiClient.get('/tesoreria/dashboard/', { params: year ? { year } : {} })).data,

  // Import histórico
  importarHistorico: async (file: File): Promise<TesoreriaImportSummary> => {
    const fd = new FormData()
    fd.append('file', file)
    return (await apiClient.post('/tesoreria/importar/', fd, { headers: { 'Content-Type': 'multipart/form-data' } })).data
  },
}

function _toFormData(data: Record<string, any>): FormData {
  const fd = new FormData()
  for (const [key, value] of Object.entries(data)) {
    if (value === undefined) continue
    if (value === null) {
      fd.append(key, '')
    } else if (value instanceof File) {
      fd.append(key, value)
    } else {
      fd.append(key, String(value))
    }
  }
  return fd
}

export default tesoreriaApi
